import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Alert,
    AlertHandle,
    autoinject,
    bind,
    FormStore,
    inject,
    Modal,
    ModalHandle,
    NothingHere,
    TasksStore,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsPencil } from '@react-icons/all-files/bs/BsPencil';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { FormGroup } from 'reactstrap';
import { AppStore } from '../../../Core/AppStore';
import { FavoriteButton } from '../../../Core/FavoriteButton';
import { NocServersStore } from '../../../NocServers/NocServersStore';
import { ActiveDisplay } from '../../ActiveDisplay';
import { DisplaysStore } from '../../DisplaysStore';
import { DisplayForm } from '../DisplayForms/DisplayForm';

export interface IDisplayEditModalProps {
    readonly appStore?: AppStore;
    readonly displaysStore?: DisplaysStore;
    readonly display: ActiveDisplay;
    readonly nocServersStore?: NocServersStore;
    readonly modalHandle: ModalHandle;
}

@autoinject
@observer
export class DisplayEditModal extends React.Component<IDisplayEditModalProps> {
    public render() {
        return (
            <Modal
                title={this.title}
                modalHandle={this.props.modalHandle}
                footer={this.footer}
                size="xl"
                className="my-2"
                onOpened={this.loadAsync}
            >
                <Alert alertHandle={this.alertHandle} />
                {this.display && (
                    <div className="container-fluid px-0">
                        {!this.loading && this.display?.displayConnectionData?.automaticallyManaged && (
                            <div className="text-center">
                                Note: Board IP address and NOC server is Automatically Managed.
                            </div>
                        )}

                        {this.displayForm && <DisplayForm displayForm={this.displayForm} display={this.display} displayLoginPoll={this.displayLoginPoll} />}

                        <NothingHere
                            empty={!this.displayForm}
                            icon={<BsPencil />}
                            loading={this.loading}
                            loadingMessage="Loading Configuration..."
                        />
                    </div>
                )}
            </Modal>
        );
    }

    @computed
    private get title() {
        if (this.display)
            return (
                <div className="w-100 text-center">
                    <FavoriteButton className="align-self-center float-start ms-3" display={this.display} />

                    <span className="fw-bold">
                        <BsPencilSquare /> Edit: {this.display?.serialNumber}
                    </span>
                </div>
            );
    }

    @computed
    private get footer() {
        return (
            <div className="w-100 d-flex justify-content-around">
                <div className="w-100 d-flex justify-content-around">
                    <button className="btn btn-secondary" onClick={this.props.modalHandle.close}>
                        Cancel
                    </button>

                    <FormGroup className="mb-0">
                        <div className="col-md-offset-2">
                            <button type="submit" className="btn btn-primary" onClick={this.submitAsync}>
                                Submit
                            </button>
                        </div>
                    </FormGroup>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async loadAsync() {
        this.loading = true;
        try {
            await this.nocServersStore.loadAllAsync(true);
            await this.displaysStore.loadAsync(this.props.display.serialNumber, {
                params: {
                    performance: true,
                    config: true,
                    telemetry: true,
                },
            });

            if (this.display?.serialNumber) await this.displaysStore.loadDisplayLoginAsync(this.display?.serialNumber);

            const lat = this.gpsLatitude ? this.gpsLatitude : this.configLatitude;
            const lng = this.gpsLongitude ? this.gpsLongitude : this.configLongitude;

            const displayConnectionData = this.display?.displayConnectionData ?? {
                active: false,
                automaticallyManaged: false,
                cameraIP: null,
                comments: null,
                customerDisplayID: null,
                customerDisplayID2: null,
                displayBoardIP: null,
                displayBoardPort: 7100,
                displayBoardWebsitePort: 443,
                input1IP: null,
                input1Password: null,
                input2IP: null,
                input2Password: null,
                modemIP: null,
                nocServer: null,
                sslPort: 22,
            };

            const unitLocation = {
                address1: this.display?.unitLocation?.address1,
                address2: this.display?.unitLocation?.address2,
                city: this.display?.unitLocation?.city,
                country: this.display?.unitLocation?.country,
                customerSiteID: this.display?.unitLocation?.customerSiteID,
                customerSiteID2: this.display?.unitLocation?.customerSiteID2,
                description: this.display?.unitLocation?.description,
                postalCode: this.display?.unitLocation?.postalCode,
                stateOrProvince: this.display?.unitLocation?.stateOrProvince,
                latitude: lat ?? this.display?.unitLocation?.latitude,
                longitude: lng ?? this.display?.unitLocation?.longitude,
            };

            this.displayForm = new FormStore<Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel>({
                serialNumber: this.display?.serialNumber,
                contract: this.display?.contract,
                displayConnectionData,
                unitLocation,
            } as any);
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async submitAsync(event: React.FormEvent<HTMLButtonElement>) {
        event.preventDefault();
        if (!this.displayForm || !this.display) {
            console.error('displayForm or display is not defined');
            this.appStore.setError(new Error('displayForm or display is not defined'));
            return;
        }
        this.displayForm.onSubmit();
        if (this.displayForm.validate()) {
            const task = this.tasksStore.startSessionTask(`Update Display ${this.display.serialNumber}`);

            const dcdFields = this.displayForm.fields.displayConnectionData.fields;
            if (dcdFields.displayBoardIP.dirty || dcdFields.nocServer.dirty)
                dcdFields.automaticallyManaged.setValue(false);

            try {
                await this.displaysStore.updateAsync(this.display.serialNumber, this.displayForm.values);

                task.complete();
                this.props.modalHandle.close();
            } catch (e: any) {
                if (e.modelState) {
                    this.displayForm.clearGlobalErrors();
                    this.displayForm.addErrors(e.modelState);
                }

                task.error(e);
            }
        } else {
            console.log('invalid');
        }
    }

    @computed
    private get gpsLatitude() {
        if (!this.display?.measurements) return undefined;

        const latitude = this.display.measurements.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude);

        return latitude ? this.calculateMeasurement(latitude) : undefined;
    }

    @computed
    private get gpsLongitude() {
        if (!this.display?.measurements) return undefined;

        const longitude = this.display.measurements.find(s => s.id == Mri.WebApi.MeasurementId.GpsLongitude);

        return longitude ? this.calculateMeasurement(longitude) : undefined;
    }

    @computed
    private get configLatitude() {
        return this.display?.configPoll?.geoVuConfig?.latitude;
    }

    @computed
    private get configLongitude() {
        return this.display?.configPoll?.geoVuConfig?.longitude;
    }

    @bind
    @action
    private calculateMeasurement(measurement: Mri.WebApi.Measurement) {
        if (measurement.floatValue != null) {
            const value = measurement.floatValue;

            return value;
        }
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.display.serialNumber);
    }

    @computed
    private get displayLoginPoll() {
        return this.displaysStore.displayLogins.get(this.props.display.serialNumber) ?? {} as Mri.DisplaySystem.DisplayLoginPoll;
    }

    @observable
    private displayForm?: FormStore<Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel>;
    @observable
    private loading = false;
    private alertHandle = new AlertHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private nocServersStore!: NocServersStore;
    @inject
    private tasksStore!: TasksStore;
}
